<template>
  <div
    v-editable="blok"
    class="SectionHeadingBlock layout-container"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md'), {
      'text-center' : blok.textAlign === 'center',
    }]"
  >
    <div
      class="flex gap-16 desk:gap-24"
      :class="{
        'flex-col desk:flex-row items-start desk:items-center desk:justify-between' : blok.textAlign == 'left',
        'flex-col' : blok.textAlign !== 'left',
      }"
    >
      <div
        class="flex flex-col gap-0 desk:gap-2"
        :class="{
          'items-center' : blok.textAlign === 'center'
        }"
      >
        <!-- Image -->
        <div v-if="blok.image && blok.image.filename" class="mb-18">
          <img
            :src="blok.image.filename"
            :alt="blok.image.alt"
            class="inline-block w-48 h-48 desk:w-64 desk:h-64"
          >
        </div>

        <!-- Tagline -->
        <div v-if="blok.tagline" class="type-tagline-sm mb-8">
          {{ blok.tagline }}
        </div>

        <!-- Headline -->
        <component
          :is="useTag"
          v-if="blok.heading"
          class="block"
          :class="{
            'type-headline-sm' : blok.size === 'xs',
            'type-headline-base' : blok.size === 'small',
            'type-headline-md' : blok.size === 'medium',
            'type-headline-lg' : blok.size === 'large',
            'type-headline-2xl' : blok.size === 'xl',
            'type-headline-3xl' : blok.size === 'xxl',
          }"
        >
          {{ blok.heading }}
        </component>

        <!-- Paragraph -->
        <div
          v-if="blok.text"
          class="mt-8 empty:hidden type-base text-text-subtle"
          v-html="renderRichText(blok.text)"
        />
      </div>

      <!-- Button wrap -->
      <div
        v-if="blok.buttonText"
        class="flex mobOnly:flex-wrap gap-12 desk:gap-16"
        :class="{
          'justify-start items-start' : blok.textAlign === 'left',
          'items-center justify-center' : blok.textAlign === 'center',
          'flex-wrap' : blok.textAlign === 'stacked',
        }"
      >
        <nuxt-link
          v-if="blok.buttonText"
          :to="$sbLinkFix(blok.buttonLink)"
          :target="$sbLinkTarget(blok.buttonLink)"
        >
          <div
            class="btn"
            :class="{
              'btn--secondary' : blok.buttonStyle === 'secondary',
              'btn--underline' : blok.buttonStyle === 'underline',
            }"
          >
            {{ blok.buttonText }}
          </div>
        </nuxt-link>
        <nuxt-link
          v-if="blok.secondaryButtonText"
          :to="$sbLinkFix(blok.secondaryButtonLink)"
          :target="$sbLinkTarget(blok.secondaryButtonLink)"
        >
          <div
            class="btn"
            :class="{
              'btn--secondary' : blok.secondaryButtonStyle === 'secondary',
              'btn--underline' : blok.secondaryButtonStyle === 'underline',
            }"
          >
            {{ blok.secondaryButtonText }}
          </div>
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { StoryBlokAsset, StoryBlokLink, StoryBlokPropBase } from '~/constants/types/storyblok';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import { ISbRichtext } from 'storyblok-js-client';

type Props = StoryBlokPropBase & {
  tagline: string;
  heading: string;
  size: 'xs' | 'small' | 'medium' | 'large' | 'xl' | 'xxl';
  textAlign: 'left' | 'center' | 'stacked';
  text: ISbRichtext,
  image: StoryBlokAsset,
  buttonText: string;
  buttonStyle: 'primary' | 'secondary' | 'underline';
  buttonLink: StoryBlokLink;
  secondaryButtonText: string;
  secondaryButtonStyle: 'primary' | 'secondary' | 'underline';
  secondaryButtonLink: StoryBlokLink;
  tag?: string
}

const props = defineProps<{
  blok: Props,
}>();

const useTag = computed(()=> {
  return props.blok.tag && props.blok.tag !== '' ? props.blok.tag : 'p';
});

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);
</script>
